import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {AgentQuotaSnapshot} from '../types/agent-quota-snapshot'

/**
 * Quota banner / disabled-input state machine.
 *
 * Mirrors the CFI and CB/CE UX guides:
 *   - https://github.com/github/token-based-billing-notes/blob/main/docs/integrator-guides/cfi-ux-guidance.md
 *   - https://github.com/github/token-based-billing-notes/blob/main/docs/integrator-guides/tbb-client-integration-guide.md#commercial-plans-businessenterprise
 *
 * The shared helper guarantees the banner (above the input) and the inline
 * disabled-input copy (when the user already has a task open) never drift.
 */
export type QuotaBannerState =
  | {kind: 'none'}
  | {
      kind: 'approachingLimit'
      percentUsed: number
      isIndividual: boolean
      canUpgradePlan: boolean
      overageMode: boolean
    }
  | {kind: 'cfiBlocked'; quotaResetDate?: string; canUpgradePlan: boolean}
  | {kind: 'cfiBudgetHit'; canUpgradePlan: boolean}
  | {kind: 'ceCbBlocked'}

const APPROACHING_LIMIT_THRESHOLD = 0.25
// Severity cutoff: percent_remaining at or below this value bumps the banner
// variant from `info` to `warning` to match the rest of the Copilot Chat UI.
const HIGH_WARNING_THRESHOLD = 0.1

export function getQuotaBannerState(snapshot: AgentQuotaSnapshot | undefined): QuotaBannerState {
  if (!snapshot) return {kind: 'none'}

  const plan = snapshot.copilot_plan
  const isIndividual = plan !== CopilotPlan.Business && plan !== CopilotPlan.Enterprise

  if (!snapshot.can_start_session) {
    if (!isIndividual) {
      return {kind: 'ceCbBlocked'}
    }

    // CFI users: differentiate "no overage allowed → enable additional usage"
    // from "overage allowed but budget cap hit → increase budget" per the CFI
    // UX guide.
    //
    // KNOWN LIMITATION: `overage_permitted` flips between true/false as the
    // user moves through the budget lifecycle (set budget > 0 → true; hit
    // budget → false; increase budget → true), so `overage_permitted === false`
    // is ambiguous — it could mean "user never enabled additional usage" or
    // "user enabled it and then hit their budget cap". We pick the safer
    // "Enable additional usage" CTA for the false branch because the
    // destination URL (MANAGE_BUDGET_URL) is the same budget-settings page in
    // both cases, so users in either state end up where they need to go.
    //
    // If `overage_permitted` is missing (older server before the field was
    // added), default to the cfiBlocked copy for the same reason.
    if (snapshot.overage_permitted === true) {
      return {kind: 'cfiBudgetHit', canUpgradePlan: snapshot.can_upgrade_plan}
    }
    return {
      kind: 'cfiBlocked',
      quotaResetDate: snapshot.quota_reset_date,
      canUpgradePlan: snapshot.can_upgrade_plan,
    }
  }

  // percent_remaining is normalized to [0, 1]. When the user is approaching
  // their limit, surface a dynamic "used X%" value so the warning escalates
  // naturally as remaining drops (75% used at 25% remaining → 99% used at
  // 1% remaining). Clamp to [0, 100] in case the server emits a slightly
  // out-of-range value.
  const remaining = snapshot.percent_remaining
  if (remaining >= 0 && remaining <= APPROACHING_LIMIT_THRESHOLD) {
    const percentUsed = Math.min(100, Math.max(0, Math.round((1 - remaining) * 100)))
    // overage_permitted is only meaningful for CFI; CB/CE users always see
    // "included AI credits" copy regardless of the flag.
    const overageMode = isIndividual && snapshot.overage_permitted === true
    return {
      kind: 'approachingLimit',
      percentUsed,
      isIndividual,
      canUpgradePlan: snapshot.can_upgrade_plan,
      overageMode,
    }
  }

  return {kind: 'none'}
}

/**
 * True when the approachingLimit banner should escalate from `info` to
 * `warning` severity. Derived from the dynamic percentUsed value so the
 * threshold (>=90% used, i.e. <=10% remaining) is documented in one place.
 */
export function isHighSeverityApproachingLimit(percentUsed: number): boolean {
  return percentUsed >= Math.round((1 - HIGH_WARNING_THRESHOLD) * 100)
}

/**
 * Formats an ISO 8601 date (YYYY-MM-DD) as a human-readable "Month D" string,
 * e.g. "April 7". Returns undefined if the input is missing or invalid so
 * callers can omit the date suffix gracefully.
 */
export function formatQuotaResetDate(isoDate: string | undefined): string | undefined {
  if (!isoDate) return undefined
  // Parse as UTC to avoid TZ-dependent off-by-one when the server emits a
  // date-only value (no time component).
  const parsed = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return undefined
  return new Intl.DateTimeFormat(undefined, {month: 'long', day: 'numeric', timeZone: 'UTC'}).format(parsed)
}
